import React, { useEffect, useState } from "react";

import OperationApi from "../../services/OperationApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import AreaApi from "../../services/AreaApi";
import LineApi from "../../services/LineApi";
import SublineApi from "../../services/SublineApi";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useNavigate } from "react-router-dom";
import { classNames } from "primereact/utils";
import { useAuth } from "../../context/AuthContext";
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

type RequiredErrorState = {
  unidade: boolean;
  area: boolean;
  linha: boolean;
  sublinha: boolean;
  nome: boolean;
  descricao: boolean;
  rank: boolean;
  tipo: boolean;
  status: boolean;
};

const createRequiredErrorsState = (): RequiredErrorState => ({
  unidade: false,
  area: false,
  linha: false,
  sublinha: false,
  nome: false,
  descricao: false,
  rank: false,
  tipo: false,
  status: false,
});

const CreateOperationForm: React.FC = () => {
  const { plantId, userId } = useAuth();

  const [unidades, setUnidades] = useState([]);
  const [areas, setAreas] = useState([]);
  const [linhas, setLinhas] = useState([]);
  const [sublinhas, setSublinhas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const { t: translate } = useTranslation("form");
  const { displayToast } = useToast();
  const navigate = useNavigate();

  interface Unidade {
    id: string;
    name: string;
  }
  const [unidade, setUnidade] = useState<Unidade | null>(null);
  interface Area {
    id: string;
    name: string;
  }
  const [area, setArea] = useState<Area | null>(null);
  interface Linha {
    id: string;
    name: string;
  }
  const [linha, setLinha] = useState<Linha | null>(null);
  interface Sublinha {
    id: string;
    name: string;
  }
  interface Type {
    id: string;
    name: string;
  }
  const [sublinha, setSublinha] = useState<Sublinha | null>(null);

  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [rank, setRank] = useState("");
  const [tipo, setTipo] = useState<Type | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [errors, setErrors] = useState({ nome: "", descricao: "", rank: "" });
  const [requiredErrors, setRequiredErrors] = useState<RequiredErrorState>(
    createRequiredErrorsState,
  );
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const validate: any = () => {
    const newErrors = { nome: "", descricao: "", rank: "" };
    if (nome.length > 50) {
      newErrors.nome = translate("NameExceptionCharacters");
    }
    if (descricao.length > 500) {
      newErrors.descricao = translate("DescriptionExceptionCharacters");
    }
    if (rank.length > 3) {
      newErrors.rank = translate("RankExceptionCharacters");
    }
    setErrors(newErrors);
    return !newErrors.nome && !newErrors.descricao && !newErrors.rank;
  };

  const statusOptions = [
    { label: "Ativa", value: "1" },
    { label: "Inativa", value: "0" },
  ];

  const requiredFieldMessage =
    translate("requiredField") ||
    translate("requiredFieldsMissing") ||
    translate("requiredFields") ||
    "Campo obrigatório";

  useEffect(() => {
    const fetchInitialData = async (): Promise<void> => {
      if (!plantId) return;
      try {
        const [unidadesResponse, tiposResponse] = await Promise.all([
          ProductionUnitApi.GetProductUnitByPlant(plantId),
          OperationApi.GetAllOperationTypes(),
        ]);
        setUnidades(unidadesResponse.data);
        setTipos(tiposResponse.data);
        setUnidade(null);
        setArea(null);
        setLinha(null);
        setSublinha(null);
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        displayToast("error", translate("errorSearch"), "", false, 3000);
      }
    };

    fetchInitialData();
  }, [plantId]);

  useEffect(() => {
    if (unidade) {
      AreaApi.GetAreaByProductionUnit(unidade.id).then((res) =>
        setAreas(res.data),
      );
      setArea(null);
      setLinha(null);
      setSublinha(null);
    }
  }, [unidade]);

  useEffect(() => {
    if (area) {
      LineApi.GetLinesByArea(area.id).then((res: any) => setLinhas(res.data));
      setLinha(null);
      setSublinha(null);
    }
  }, [area]);

  useEffect(() => {
    if (linha) {
      SublineApi.GetSublineByLine(linha.id).then((res: any) =>
        setSublinhas(res.data),
      );
      setSublinha(null);
    }
  }, [linha]);

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();

    const trimmedNome = nome.trim();
    const trimmedDescricao = descricao.trim();
    const trimmedRank = rank.trim();

    const nextRequiredErrors: RequiredErrorState = {
      unidade: !unidade,
      area: !area,
      linha: !linha,
      sublinha: !sublinha,
      nome: !trimmedNome,
      descricao: !trimmedDescricao,
      rank: !trimmedRank,
      tipo: !tipo,
      status: status === null,
    };

    if (Object.values(nextRequiredErrors).some(Boolean)) {
      setRequiredErrors(nextRequiredErrors);

      const missingFields: string[] = [];
      if (nextRequiredErrors.unidade)
        missingFields.push(translate("unit") || "Unidade");
      if (nextRequiredErrors.area)
        missingFields.push(translate("area") || "Área");
      if (nextRequiredErrors.linha)
        missingFields.push(translate("line") || "Linha");
      if (nextRequiredErrors.sublinha)
        missingFields.push(translate("subline") || "Sublinha");
      if (nextRequiredErrors.nome)
        missingFields.push(translate("name") || "Nome");
      if (nextRequiredErrors.descricao)
        missingFields.push(translate("description") || "Descrição");
      if (nextRequiredErrors.rank)
        missingFields.push(translate("operationRank") || "Rank");
      if (nextRequiredErrors.tipo)
        missingFields.push(translate("Type") || "Tipo");
      if (nextRequiredErrors.status)
        missingFields.push(translate("status") || "Status");

      const summary = translate("error") || "Erro";
      const requiredMessage =
        translate("requiredFieldsMissing") ||
        translate("requiredFields") ||
        translate("requiredField") ||
        "Campos obrigatórios";

      displayToast(
        "error",
        summary,
        `${requiredMessage}: ${missingFields.join(", ")}`,
        false,
        3000,
      );
      return;
    }

    setRequiredErrors(createRequiredErrorsState());

    try {
      const payload = {
        name: trimmedNome,
        description: trimmedDescricao,
        rank: Number(trimmedRank),
        isActive: status === "1",
        subLineId: sublinha!.id,
        operationTypeId: tipo!.id,
        createdBy: userId,
        updatedBy: userId,
      };

      if (validate()) {
        await OperationApi.Create(payload);
        displayToast(
          "success",
          "Sucesso",
          translate("operationCreatedSuccess"),
          false,
          3000,
        );
        setTimeout(() => navigate("/operations"), 1000);
      }
    } catch (error: any) {
      const backendMessage =
        error?.data?.data ||
        error?.data?.data?.[0] ||
        "Erro ao cadastrar operação.";

      if (error.response?.status === 400 || backendMessage) {
        displayToast("error", "Erro", backendMessage, false, 3000);
      } else {
        displayToast("error", "Erro", backendMessage, false, 3000);
      }
    }
  };

  return (
    <FormCard
      title={translate("Operation")}
      icon="precision_manufacturing"
      infoIcon="info"
      infoText="Preencha os dados primários da operação."
      submitButtonLabel={translate("register")}
      cancelButtonLabel={translate("Cancel")}
      onSubmit={handleSave}
      onCancel={() => setShowConfirmCancel(true)}
      numColumns={6}
    >
      {/* Unidade */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("productionUnit")}
          required={true}
          value={unidade}
          options={unidades}
          optionLabel="name"
          placeholder={translate("selectUnit")}
          className={classNames("w-full", { "p-invalid border-red": requiredErrors.unidade })}
          onChange={(val: any) => {
            setUnidade(val);
            setRequiredErrors((prev) => ({ ...prev, unidade: false, area: false, linha: false, sublinha: false }));
          }}
        />
        {requiredErrors.unidade && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      {/* Área */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("area")}
          required={true}
          value={area}
          options={areas}
          optionLabel="name"
          placeholder={translate("selectArea")}
          disabled={!unidade}
          className={classNames("w-full", { "p-invalid border-red": requiredErrors.area })}
          onChange={(val: any) => {
            setArea(val);
            setRequiredErrors((prev) => ({ ...prev, area: false, linha: false, sublinha: false }));
          }}
        />
        {requiredErrors.area && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      {/* Linha */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("line")}
          required={true}
          value={linha}
          options={linhas}
          optionLabel="name"
          placeholder={translate("selectLine")}
          disabled={!area}
          className={classNames("w-full", { "p-invalid border-red": requiredErrors.linha })}
          onChange={(val: any) => {
            setLinha(val);
            setRequiredErrors((prev) => ({ ...prev, linha: false, sublinha: false }));
          }}
        />
        {requiredErrors.linha && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      {/* Sublinha */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("subline")}
          required={true}
          value={sublinha}
          options={sublinhas}
          optionLabel="name"
          placeholder={translate("selectSubline")}
          disabled={!linha}
          className={classNames("w-full", { "p-invalid border-red": requiredErrors.sublinha })}
          onChange={(val: any) => {
            setSublinha(val);
            setRequiredErrors((prev) => ({ ...prev, sublinha: false }));
          }}
        />
        {requiredErrors.sublinha && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      {/* Nome da Operação */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("operationName")}
          required={true}
          value={nome}
          onChange={(val: any) => {
            setNome(val);
            setRequiredErrors((prev) => ({ ...prev, nome: false }));
          }}
          placeholder={translate("operationName")}
          className={classNames("w-full", { "p-invalid border-red": requiredErrors.nome || errors.nome })}
        />
        {requiredErrors.nome && <small className="p-error">{requiredFieldMessage}</small>}
        {!requiredErrors.nome && errors.nome && <small className="p-error">{errors.nome}</small>}
      </div>

      {/* Descrição */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="textArea"
          label={translate("operationDescription")}
          required={true}
          value={descricao}
          onChange={(val: any) => {
            setDescricao(val);
            setRequiredErrors((prev) => ({ ...prev, descricao: false }));
          }}
          placeholder={translate("operationDescription")}
          rows={3}
          maxLength={600}
          className={classNames("w-full", { "p-invalid border-red": requiredErrors.descricao || errors.descricao })}
        />
        {requiredErrors.descricao && <small className="p-error">{requiredFieldMessage}</small>}
        {!requiredErrors.descricao && errors.descricao && <small className="p-error">{errors.descricao}</small>}
      </div>

      {/* Rank */}
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="text"
          label={translate("operationRank")}
          required={true}
          value={rank}
          onChange={(val: any) => {
            setRank(val);
            setRequiredErrors((prev) => ({ ...prev, rank: false }));
          }}
          placeholder={translate("operationRank")}
          className={classNames("w-full", { "p-invalid": requiredErrors.rank || Boolean(errors.rank) })}
        />
        {requiredErrors.rank && <small className="p-error">{requiredFieldMessage}</small>}
        {!requiredErrors.rank && errors.rank && <small className="p-error">{errors.rank}</small>}
      </div>

      {/* Tipo */}
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translate("Type")}
          required={true}
          value={tipo}
          options={tipos}
          optionLabel="name"
          placeholder={translate("Type")}
          className={classNames("w-full", { "p-invalid border-red": requiredErrors.tipo })}
          onChange={(val: any) => {
            setTipo(val);
            setRequiredErrors((prev) => ({ ...prev, tipo: false }));
          }}
        />
        {requiredErrors.tipo && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      {/* Status */}
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translate("status")}
          required={true}
          value={status}
          options={statusOptions}
          placeholder={translate("status")}
          className={classNames("w-full", { "p-invalid border-red": requiredErrors.status })}
          onChange={(val: any) => {
            setStatus(val);
            setRequiredErrors((prev) => ({ ...prev, status: false }));
          }}
        />
        {requiredErrors.status && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/operations")}
      />
    </FormCard>
  );
};

export default CreateOperationForm;
